import { SyntheticEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Autocomplete, Button, TextField, Typography } from '@mui/material'

import { useLanguages, useMe, useSetUserLanguage } from 'src/hooks'

import { useStyles } from './styles'
import { ETranslationNs, ILanguageBaseWithId } from 'src/types'
import { HelperWrapper } from 'src/components/helper-wrapper'

interface IProps {
  handleClose: () => void
}

const UserLanguageForm = ({ handleClose }: IProps) => {
  const [t] = useTranslation(ETranslationNs.UserProfile)
  const { data: user } = useMe()
  const { data: languages } = useLanguages({ enabled: true, query: '' })
  const { mutateAsync: changeLanguage, isLoading } = useSetUserLanguage()
  const [language, setLanguage] = useState<null | ILanguageBaseWithId>(user?.language || null)
  const [error, setError] = useState<string | null>(null)

  const classes = useStyles()

  const handleLanguageChange = (
    _: SyntheticEvent<Element, Event>,
    value: ILanguageBaseWithId | null,
  ) => {
    setLanguage(value)
    setError(null)
  }

  const handleLanguageSubmit = async () => {
    if (!language) {
      return setError(t('language-input-error.v'))
    }

    if (language === user?.language) {
      return handleClose()
    }

    setError(null)
    const resp = await changeLanguage(language.id)
    if (!resp) return
    window.location.reload()
    handleClose()
  }

  if (!user?.language) return null

  return (
    <>
      <div className={classes.upperWrapper}>
        <Typography className={classes.title}>
          <HelperWrapper t="user-language-form-title" ns={ETranslationNs.UserProfile} />
        </Typography>
        <Autocomplete
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={language!}
          disablePortal
          loading={!languages}
          options={languages?.data || []}
          getOptionLabel={(option) => option.name}
          sx={{ width: '100%' }}
          onChange={handleLanguageChange}
          disableClearable
          renderInput={(params) => (
            <TextField
              helperText={error}
              FormHelperTextProps={{ error: !!error }}
              {...params}
              autoComplete="off"
              label={t('language-input-label.v')}
              InputLabelProps={{ className: classes.inputLabel }}
            />
          )}
        />
      </div>
      <Button
        fullWidth
        variant="contained"
        onClick={handleLanguageSubmit}
        type="button"
        disabled={isLoading || !!error}
      >
        <HelperWrapper t="user-language-form-submit" ns={ETranslationNs.UserProfile} />
      </Button>
    </>
  )
}

export { UserLanguageForm }
